import { Clock, Heart, Phone } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useI18n } from "@project/lib/use-i18n";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@project/components/ui/dialog";

export type VisitorSuccessDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitorName?: string;
};

function AnimatedSuccessIcon() {
  return (
    <div className="relative mx-auto mb-5 h-20 w-20">
      <span
        aria-hidden
        className="admission-success-icon__ripple bg-gold/25 ring-gold/15 absolute inset-0 rounded-full ring-4"
      />
      <div className="admission-success-icon bg-gold/20 ring-gold/10 relative grid h-full w-full place-items-center rounded-full ring-4">
        <svg
          viewBox="0 0 72 72"
          className="h-11 w-11 text-gold"
          fill="none"
          aria-hidden
        >
          <circle
            className="admission-success-icon__circle"
            cx="36"
            cy="36"
            r="28"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
          />
          <path
            className="admission-success-icon__check"
            d="M24 37.5 32.5 46 48 28.5"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

export function VisitorSuccessDialog({
  open,
  onOpenChange,
  visitorName,
}: VisitorSuccessDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-gold/20 max-w-2xl gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <div className="from-gold/15 via-gold/5 bg-gradient-to-b to-background px-8 pb-7 pt-9 text-center">
          <AnimatedSuccessIcon />
          <DialogTitle className="font-serif text-2xl font-bold text-foreground sm:text-3xl">
            {t("account.visitorSuccessDialog.title")}
          </DialogTitle>
          <DialogDescription className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t("account.visitorSuccessDialog.description")}
          </DialogDescription>
          {visitorName ? (
            <div className="bg-card/80 mx-auto mt-6 max-w-xl overflow-hidden rounded-xl border border-border px-5 py-4 text-left shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                {t("account.visitorSuccessDialog.visitor")}
              </p>
              <p className="mt-1 font-medium text-foreground">{visitorName}</p>
            </div>
          ) : null}
        </div>
        <div className="space-y-5 px-8 pb-8">
          <div>
            <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              {t("account.visitorSuccessDialog.whatHappensNext")}
            </p>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground">
                <Heart className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span>{t("account.visitorSuccessDialog.step1")}</span>
              </li>
              <li className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span>
                  {t("account.visitorSuccessDialog.contactBefore")}{" "}
                  <span className="font-semibold text-foreground">
                    {t("account.visitorSuccessDialog.workingDays")}
                  </span>{" "}
                  {t("account.visitorSuccessDialog.contactAfter")}
                </span>
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              className="hover:bg-gold/90 h-11 flex-1 rounded-xl bg-gold text-gold-foreground"
              onClick={() => onOpenChange(false)}
            >
              {t("account.visitorSuccessDialog.done")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-11 flex-1 rounded-xl hover:bg-primary hover:text-primary-foreground"
              asChild
            >
              <Link to="/">{t("account.visitorSuccessDialog.backToHome")}</Link>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
